// @flow

/**
 * getSchemaDefault returns a default schema value for a given schema property.
 * Note the default value is not taken from the default property, for now it
 * will be either null or the first entry of either oneOf or anyOf if an enum.
 */
export function getSchemaDefault(name: string, schema: Object = {}) {
  if (!schema || !schema.properties) return null;
  const prop = schema.properties && schema.properties[name];

  if (!prop) {
    return Object.values(schema.properties).reduce((acc: mixed, v: mixed) => {
      if (v && typeof v === "object" && v.type === "object" && v.properties) {
        return getSchemaDefault(name, v) || acc;
      }
      return acc;
    }, null);
  }

  if (prop && prop.type !== "object" && (prop.oneOf || prop.anyOf)) {
    return (prop.oneOf || prop.anyOf)[0].const;
  }

  if (
    prop.type === "object" &&
    prop.properties &&
    prop.properties.id &&
    (prop.properties.id.oneOf || prop.properties.id.anyOf)
  ) {
    return { id: (prop.properties.id.oneOf || prop.properties.id.anyOf)[0].const };
  }

  return null;
}

/**
 * transformInput takes the schema form's input, formData and dataSchema are
 * transforms nulls to zero values that the form understands.
 *
 * Rules:
 * - nulls (no enum) become undefined
 * - nulls (with enums) become the first object in either anyOf, oneOf
 *
 */
export function transformInput(data: Object, schema: Object): Object {
  return Object.entries(data).reduce((acc: Object, [k, v]: [string, mixed]) => {
    const value = v === null ? getSchemaDefault(k, schema) : v;
    const transformedValue = value === null ? undefined : value;

    if (
      transformedValue &&
      typeof transformedValue === "object" &&
      !Array.isArray(transformedValue)
    ) {
      return Object.assign({}, acc, { [k]: transformInput(transformedValue, schema) });
    }

    return Object.assign({}, acc, { [k]: transformedValue });
  }, {});
}

/**
 * transformOutput takes the schema form's output (i.e. formData object) and
 * transforms known zero values into required nulll before sending on to backend
 * services
 *
 * Rules:
 * - any underfined object become null
 * - strings that are either empty of "null" become null
 * - numbers assigned -1 become null
 * - objects with id property assigned -1 become null
 */
export function transformOutput(data: Object): Object {
  if (data && data.hasOwnProperty("length")) return data;
  return Object.entries(data).reduce((acc: Object, [k, v]: [string, mixed]) => {
    let transformedValue = v;
    if (typeof v === "undefined") {
      transformedValue = null;
    }

    if (typeof v === "string" && (v === "null" || v === "")) {
      transformedValue = null;
    }

    // Leave the comments here
    if (typeof v === "number" && v === -1) {
      transformedValue = null;
    }

    if (v && typeof v === "object" && v.id === -1) {
      transformedValue = null;
    }

    if (
      transformedValue &&
      typeof transformedValue === "object" &&
      !Array.isArray(transformedValue) &&
      transformedValue.type !== "div"
    ) {
      return Object.assign({}, acc, { [k]: transformOutput(transformedValue) });
    }

    return Object.assign({}, acc, { [k]: transformedValue });
  }, {});
}
